import React, { useMemo } from 'react';
import {
  EditBase,
  Empty,
  useGetOne,
  useNotify,
} from 'react-admin';

import {
  Box,
  CircularProgress,
} from '@mui/material';

import { PermissionRole, ReformedPermissionRole } from './type';
import resourceNames from '../../constants/resourceNames';
import { permissionRoleCleaner } from '../../utilities/dataProvider/cleaner';
import PermissionRolesForm from './PermissionRolesForm';
import errorNotification from '../../utilities/errorNotification';

const PermissionRolesEdit = ({
  id,
  setEdit,
}: {
  id: string;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { data, isLoading } = useGetOne<PermissionRole>(resourceNames.PERMISSION_ROLES, { id });
  const notify = useNotify();

  const transformedData: ReformedPermissionRole | undefined = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      permissions: data.permissions.reduce((acc, { name, attributes }) => ({
        ...acc,
        [name]: {
          enabled: true,
          attributes,
        },
      }), {}),
    };
  }, [data]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return <Empty />;

  return (
    <EditBase
      id={id}
      redirect={false}
      transform={permissionRoleCleaner}
      mutationOptions={{
        onSuccess: () => setEdit(false),
        onError: (e) => {
          notify(errorNotification(e), { type: 'error' });
        },
      }}
      mutationMode="pessimistic"
    >
      <PermissionRolesForm
        mode="edit"
        onCancel={() => setEdit(false)}
        transformedData={transformedData}
      />
    </EditBase>
  );
};

export default PermissionRolesEdit;
